import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Select, Box, Text, Loader, Group, ActionIcon, Tooltip } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { entityService } from '../../services/entityServiceSingleton';
import { EntityBrowserDialog } from '../dialogs';

export type WorkflowKind = 'Request' | 'Authorization';

interface WorkflowSelectorProps {
    workflowKind: WorkflowKind;
    value: string | null;
    onChange: (value: string | null) => void;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    description?: React.ReactNode;
}

const WORKFLOW_KIND_CONFIG: Record<
    WorkflowKind,
    { scimFilter: string; browserTitle: string; placeholder: string }
> = {
    Request: {
        scimFilter: 'Type eq "Request"',
        browserTitle: 'Select Request Workflow',
        placeholder: 'Select a request workflow...',
    },
    Authorization: {
        scimFilter: 'Type eq "Authorization"',
        browserTitle: 'Select Authorization Workflow',
        placeholder: 'Select an authorization workflow...',
    },
};

export const WorkflowSelector: React.FC<WorkflowSelectorProps> = ({
    workflowKind,
    value,
    onChange,
    label = 'Workflow',
    required = false,
    disabled = false,
    error = false,
    helperText,
    description,
}) => {
    const config = WORKFLOW_KIND_CONFIG[workflowKind];
    const [browserOpen, setBrowserOpen] = useState(false);
    const [workflows, setWorkflows] = useState<Array<{ Id: string; DisplayName: string }>>([]);
    const [loading, setLoading] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState('');

    const fetchedRef = useRef(false);
    const failedRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (fetchedRef.current) return;
        fetchedRef.current = true;
        setLoading(true);
        entityService.getEntitiesByType('Workflow', 1, 100, undefined, config.scimFilter, ['Id', 'DisplayName'])
            .then((response) => {
                const items = (response?.Resources ?? []) as any[];
                setWorkflows(items.map((w: any) => ({
                    Id: w.Id || w.id || '',
                    DisplayName: w.DisplayName || w.displayName || w.Id || '',
                })));
            })
            .catch(() => setWorkflows([]))
            .finally(() => setLoading(false));
    }, [entityService, config.scimFilter]);

    useEffect(() => {
        if (!value) {
            setSelectedLabel('');
            return;
        }
        const found = workflows.find(w => w.Id === value);
        if (found) {
            setSelectedLabel(found.DisplayName);
            return;
        }
        if (failedRef.current.has(value)) return;
        entityService.getEntityById(value, 'Workflow', true, ['Id', 'DisplayName'])
            .then((full: any) => setSelectedLabel(full?.DisplayName || value))
            .catch(() => {
                failedRef.current.add(value);
                setSelectedLabel(value);
            });
    }, [value, workflows, entityService]);

    const selectData = useMemo(() => {
        const options = workflows.map(w => ({ value: w.Id, label: w.DisplayName || w.Id }));
        if (value && !workflows.find(w => w.Id === value)) {
            options.unshift({ value, label: selectedLabel || value });
        }
        return options;
    }, [workflows, value, selectedLabel]);

    const handleBrowserConfirm = (
        input: Array<{ Id: string; DisplayName?: string; EntityType?: string }> | string[]
    ) => {
        if (Array.isArray(input) && input.length > 0) {
            const first = input[0];
            const id = typeof first === 'object' && first !== null && 'Id' in first
                ? (first as { Id: string }).Id
                : String(first);
            if (id) {
                onChange(id);
                const entity = typeof first === 'object' ? first as { DisplayName?: string } : null;
                if (entity?.DisplayName) setSelectedLabel(entity.DisplayName);
            }
        }
        setBrowserOpen(false);
    };

    return (
        <Box>
            <Group gap="xs" align="flex-end">
                <Select
                    flex={1}
                    label={label}
                    placeholder={config.placeholder}
                    data={selectData}
                    value={value}
                    onChange={(val) => onChange(val || null)}
                    required={required}
                    disabled={loading || disabled}
                    error={error}
                    searchable
                    clearable
                    rightSection={loading ? <Loader size={16} /> : undefined}
                />
                <Tooltip label={config.browserTitle} withArrow>
                    <ActionIcon
                        variant="light"
                        onClick={() => setBrowserOpen(true)}
                        disabled={disabled}
                        mb={4}
                    >
                        <IconSearch size={16} />
                    </ActionIcon>
                </Tooltip>
            </Group>

            {description && (
                <Text size="xs" c="dimmed" display="block" mt={4}>
                    {description}
                </Text>
            )}

            {helperText && (
                <Text size="xs" c="dimmed" display="block" mt={4}>
                    {helperText}
                </Text>
            )}

            <EntityBrowserDialog
                open={browserOpen}
                onClose={() => setBrowserOpen(false)}
                onConfirm={handleBrowserConfirm}
                entityType="workflow"
                title={config.browserTitle}
                callerName={`WorkflowSelector (${workflowKind})`}
                singleSelection={true}
                scimFilter={config.scimFilter}
            />
        </Box>
    );
};
